// Rep-experience resolution — the one place that answers "which rep experience
// does this workspace member get?", for the signed-in user and for an admin's
// view-as target alike.
//
// Why it exists: Today, Actions and the sidebar each derived that answer
// themselves, and one of them (DashboardMain, mb-frontend#4573) treated the
// mere presence of `?viewAs=<uuid>` as an AE override. A super admin opening an
// explicitly configured SDR's Today therefore landed on the AE opportunity
// dashboard. A query parameter names *who* is being viewed; it never says
// *what* they are. Resolve the member, then choose the experience.

export type RepExperience = 'sdr' | 'ae' | 'general';

/** A drill-in experience. 'general' members have no rep dashboard to open. */
export type ViewAsExperience = Exclude<RepExperience, 'general'>;

/**
 * Identity of the member being viewed. A single param, shared by the SDR and AE
 * surfaces, because links carrying it are already in the wild — they keep
 * working and are resolved, never guessed at.
 */
export const VIEW_AS_QUERY_PARAM = 'viewAs';

/**
 * Declared intent of a view-as link ('sdr' | 'ae'), written by the pickers and
 * link builders so a pasted URL says which experience it meant to open.
 *
 * It is a hint, never an authority: it is validated against the resolved member
 * and the member always wins, so a stale, copied or hand-edited declaration can
 * never route anyone into the wrong experience. Its real job is letting a
 * surface pick the right skeleton while the roster is still loading.
 */
export const VIEW_AS_EXPERIENCE_QUERY_PARAM = 'viewAsExperience';

/** The member facts the rule needs — the shape `GET /workspaces/:id/users` returns. */
export interface RepExperienceMember {
  ui_persona?: RepExperience | null;
  is_admin?: boolean;
  owns_open_crm_deals?: boolean;
  has_active_signals?: boolean;
}

/**
 * The experience a *view-as target* has.
 *
 * Explicit `ui_persona` is authoritative in both directions — a member marked
 * 'sdr' is an SDR even when they own open CRM opportunities. Deal ownership is
 * only a fallback for members whose persona was never set, which is how
 * workspaces predating ui_persona still surface their real AEs.
 *
 * The fallback is off for anyone actively working a signal queue. Owning an open
 * opportunity is ordinary for an SDR, and the AE shell is built around the
 * pipeline, not signals — inferring AE from ownership alone moved whole queues
 * out of view: dylan.lam@jumio.com, 2026-09-25, 1,428 live signals.
 *
 * `has_active_signals` is the roster's existing flag (non-dismissed, last 30
 * days), reused rather than duplicated. The bound matters in both directions: a
 * member whose signal queue has gone quiet while their CRM deals have not is
 * exactly who the AE fallback is for. Dropping it on 2026-09-25 would have moved
 * four more members.
 *
 * This is the view-as half of mb-server's `effective_ui_persona`
 * (workspaceDbOperations.js). The server's rule carries one extra branch —
 * workspace admins are never auto-promoted into the narrowed AE shell, because
 * one junk opportunity carrying an admin's email was enough to take the full
 * workspace view away from them. That branch is deliberately absent here:
 *
 *  - for a viewer's *own* shell it is never needed client-side, because
 *    `WorkspaceProvider` reads the server's already-resolved
 *    `effective_ui_persona` rather than recomputing anything;
 *  - for a *view-as target* it would be wrong, because a super admin has named
 *    the person they want to inspect, and an admin who genuinely works a deal
 *    queue should stay inspectable.
 *
 * It was briefly an option here with no caller. A flag nothing sets is not a
 * rule, so the single real behaviour is the only one left.
 */
export function resolveRepExperience(
  member: RepExperienceMember | null | undefined,
): RepExperience {
  if (!member) return 'general';

  const persona = member.ui_persona;
  if (persona === 'sdr' || persona === 'ae') return persona;

  // Documented legacy fallback, reachable only when no persona was ever chosen
  // and the member is not working the signal queue.
  if (member.owns_open_crm_deals === true && member.has_active_signals !== true) return 'ae';

  return 'general';
}

/** Whether a member has a rep dashboard that can be opened at all. */
export function viewAsExperienceFor(
  member: RepExperienceMember | null | undefined,
): ViewAsExperience | null {
  const experience = resolveRepExperience(member);
  return experience === 'general' ? null : experience;
}

/** Validate a declared experience from the URL. Anything else is discarded. */
export function parseViewAsExperience(raw: string | null | undefined): ViewAsExperience | null {
  const value = raw?.trim().toLowerCase();
  return value === 'sdr' || value === 'ae' ? value : null;
}

/**
 * Apply (or clear) the view-as params on a query string. Mutates and returns
 * the given params so every caller — the pickers, the sidebar, the Actions deep
 * links — writes the identical pair and no surface can drift.
 */
export function applyViewAsParams(
  params: URLSearchParams,
  userId: string | null | undefined,
  experience: ViewAsExperience | null | undefined,
): URLSearchParams {
  const target = userId?.trim();
  if (!target) {
    params.delete(VIEW_AS_QUERY_PARAM);
    params.delete(VIEW_AS_EXPERIENCE_QUERY_PARAM);
    return params;
  }
  params.set(VIEW_AS_QUERY_PARAM, target);
  if (experience) {
    params.set(VIEW_AS_EXPERIENCE_QUERY_PARAM, experience);
  } else {
    params.delete(VIEW_AS_EXPERIENCE_QUERY_PARAM);
  }
  return params;
}

/** `?viewAs=…&viewAsExperience=…` for a fresh link. Empty string when no target. */
export function viewAsQueryString(
  userId: string | null | undefined,
  experience: ViewAsExperience | null | undefined,
): string {
  return applyViewAsParams(new URLSearchParams(), userId, experience).toString();
}
